import type { StateCreator } from "zustand";
import type { DraftParameter } from "@opus-dt/shared-types";
import { tmp } from "../tmpIds";
import type { DraftStoreState } from "./types";

type NestedActions = Pick<
  DraftStoreState,
  | "addParameter"
  | "updateParameter"
  | "removeParameter"
  | "addSensorLink"
  | "removeSensorLink"
  | "addCompatibility"
  | "updateCompatibility"
  | "removeCompatibility"
>;

export const createNestedSlice: StateCreator<DraftStoreState, [], [], NestedActions> = (set) => ({
  addParameter: (tmpMachineId, param) => {
    const tmpParamId = tmp.param();
    set((state) => {
      const m = state.machines[tmpMachineId];
      if (!m) return state;
      const newParam = { ...param, tmpId: tmpParamId } as DraftParameter;
      return {
        machines: {
          ...state.machines,
          [tmpMachineId]: {
            ...m,
            parameters: [...m.parameters, newParam],
          },
        },
        dirty: true,
      };
    });
    return tmpParamId;
  },

  updateParameter: (tmpMachineId, tmpParamId, patch) => {
    set((state) => {
      const m = state.machines[tmpMachineId];
      if (!m) return state;
      const updated = m.parameters.map((p) =>
        p.tmpId === tmpParamId ? ({ ...p, ...patch } as DraftParameter) : p
      );
      return {
        machines: {
          ...state.machines,
          [tmpMachineId]: { ...m, parameters: updated },
        },
        dirty: true,
      };
    });
  },

  removeParameter: (tmpMachineId, tmpParamId) => {
    set((state) => {
      const m = state.machines[tmpMachineId];
      if (!m) return state;
      return {
        machines: {
          ...state.machines,
          [tmpMachineId]: {
            ...m,
            parameters: m.parameters.filter((p) => p.tmpId !== tmpParamId),
            sensorLinks: m.sensorLinks.filter((sl) => sl.parameterTmpId !== tmpParamId),
          },
        },
        dirty: true,
      };
    });
  },

  addSensorLink: (tmpMachineId, link) => {
    const tmpLinkId = tmp.sensor();
    set((state) => {
      const m = state.machines[tmpMachineId];
      if (!m) return state;
      return {
        machines: {
          ...state.machines,
          [tmpMachineId]: {
            ...m,
            sensorLinks: [...m.sensorLinks, { ...link, tmpId: tmpLinkId }],
          },
        },
        dirty: true,
      };
    });
    return tmpLinkId;
  },

  removeSensorLink: (tmpMachineId, tmpLinkId) => {
    set((state) => {
      const m = state.machines[tmpMachineId];
      if (!m) return state;
      return {
        machines: {
          ...state.machines,
          [tmpMachineId]: {
            ...m,
            sensorLinks: m.sensorLinks.filter((sl) => sl.tmpId !== tmpLinkId),
          },
        },
        dirty: true,
      };
    });
  },

  addCompatibility: (tmpMachineId, compat) => {
    const tmpCompatId = tmp.compat();
    set((state) => {
      const m = state.machines[tmpMachineId];
      if (!m) return state;
      return {
        machines: {
          ...state.machines,
          [tmpMachineId]: {
            ...m,
            compatibilities: [...m.compatibilities, { ...compat, tmpId: tmpCompatId }],
          },
        },
        dirty: true,
      };
    });
    return tmpCompatId;
  },

  updateCompatibility: (tmpMachineId, tmpCompatId, patch) => {
    set((state) => {
      const m = state.machines[tmpMachineId];
      if (!m) return state;
      return {
        machines: {
          ...state.machines,
          [tmpMachineId]: {
            ...m,
            compatibilities: m.compatibilities.map((c) =>
              c.tmpId === tmpCompatId ? { ...c, ...patch } : c
            ),
          },
        },
        dirty: true,
      };
    });
  },

  removeCompatibility: (tmpMachineId, tmpCompatId) => {
    set((state) => {
      const m = state.machines[tmpMachineId];
      if (!m) return state;
      return {
        machines: {
          ...state.machines,
          [tmpMachineId]: {
            ...m,
            compatibilities: m.compatibilities.filter((c) => c.tmpId !== tmpCompatId),
          },
        },
        dirty: true,
      };
    });
  },
});
